import React, { useState, ChangeEvent, FocusEvent, useCallback } from 'react';
import { cn, formValidation } from '@express-labs/raven-tools';

import FloatingInput from '../FloatingInput/FloatingInput';
import Button from '../Button/Button';

import s from './FloatingPasswordInput.scss';

export interface FloatingPasswordInputProps {
  className?: string;
  disabled?: boolean;
  errorClassName?: string;
  errors?: string[];
  id?: string;
  ignoreInputValidation?: boolean;
  inputClassName?: string;
  instructionsClassName?: string;
  label: string;
  labelClassName?: string;
  name?: string;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  showHelp?: boolean;
  showPasswordButton?: boolean;
  value?: string;
}

const FloatingPasswordInput: React.FC<FloatingPasswordInputProps> = (props) => {
  const {
    className,
    errors = [],
    ignoreInputValidation = false,
    instructionsClassName,
    onBlur,
    onChange,
    showHelp,
    showPasswordButton,
    value,
    ...rest
  } = props;

  const [showPassword, setShowPassword] = useState(false);
  const [inputErrors, setInputErrors] = useState<string[]>([]);
  const [matchedInstructions, setMatchedInstructions] = useState<{
    length: boolean;
    alphabet: boolean;
    numeric: boolean;
  }>({
    length: false,
    alphabet: false,
    numeric: false,
  });

  const hasTypeError = (inputError: string[], keys: string[]): boolean =>
    inputError.some((item) => keys.includes(item));

  const setRuleError = useCallback(
    (password: string) => {
      if (!ignoreInputValidation) {
        const fieldError: string[] = [];
        const validationErrors = formValidation.testSecurePassword(password);
        const hasValidationErrors = validationErrors && validationErrors.length;

        if (password && !hasValidationErrors) {
          setInputErrors([]);
        }

        if (hasValidationErrors) {
          fieldError.push('Your password does not meet the requirements.');
        }

        setMatchedInstructions({
          length: !hasTypeError(validationErrors, ['short', 'long', 'space']),
          alphabet: !hasTypeError(validationErrors, ['upper', 'lower']),
          numeric: !hasTypeError(validationErrors, ['number']),
        });
        setInputErrors(fieldError);
      }
    },
    [ignoreInputValidation]
  );

  const handleOnBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      setRuleError(inputValue);

      if (onBlur) {
        onBlur(e);
      }
    },
    [onBlur, setRuleError]
  );

  const handleShowPasswordButtonClick = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.preventDefault();
      setShowPassword(!showPassword);
    },
    [showPassword]
  );

  const mergedErrors = inputErrors.concat(errors);

  return (
    <FloatingInput
      {...rest}
      className={cn(className, showPasswordButton && s.passwordInputContainer)}
      type={showPassword ? 'text' : 'password'}
      onChange={onChange}
      onBlur={handleOnBlur}
      value={value}
      errors={mergedErrors}
    >
      {showPasswordButton ? (
        <Button
          as="button"
          mode="solid"
          size="small"
          theme="dark"
          onClick={handleShowPasswordButtonClick}
          className={s.showPasswordButton}
          type="button"
        >
          {showPassword ? 'Hide' : 'Show'}
        </Button>
      ) : null}

      {showHelp && (
        <div className={s.passwordInstructions}>
          <ul className={cn(s.instructionList, instructionsClassName)}>
            <li className={cn(matchedInstructions.length && s.valid)}>
              7-20 characters, no spaces.
            </li>
            <li className={cn(matchedInstructions.alphabet && s.valid)}>
              At least one UPPER &amp; one lower case
            </li>
            <li className={cn(matchedInstructions.numeric && s.valid)}>
              At least one numeric character (0-9)
            </li>
          </ul>
        </div>
      )}
    </FloatingInput>
  );
};

FloatingPasswordInput.displayName = 'FloatingPasswordInput';

export default FloatingPasswordInput;
